'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState, useTransition, type ReactNode } from 'react';

import {
  catalogQueryToSearch,
  DIRECTION_LABELS,
  isCatalogQueryEmpty,
  OCCASION_LABELS,
  TYPE_LABELS,
  type CatalogQuery,
  type Color,
  type Direction,
  type Occasion,
  type ProductType,
} from '@/entities/product';
import { cx } from '@/shared/lib/cx';
import { Button } from '@/shared/ui/button';
import { Dialog } from '@/shared/ui/dialog';
import { Icon } from '@/shared/ui/icon';

import styles from './catalog-filters.module.scss';

export type CatalogFiltersProps = {
  query: CatalogQuery;
  sizes: string[];
  colors: Color[];
  total: number;
  showDemoNotes: boolean;
};

const DIRECTIONS = ['body', 'care', 'mind'] as const satisfies readonly Direction[];
const OCCASIONS = [
  'practice',
  'everyday',
  'walk',
  'home',
  'after-training',
  'gift',
] as const satisfies readonly Occasion[];
const TYPES = ['top', 'bottom', 'practice', 'objects'] as const satisfies readonly ProductType[];
const toggle = <T,>(list: readonly T[], value: T) =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

function Filters({
  query,
  sizes,
  colors,
  showDemoNotes,
  apply,
}: Omit<CatalogFiltersProps, 'total'> & { apply: (next: CatalogQuery) => void }) {
  return (
    <div className={styles.groups}>
      <fieldset className={styles.group}>
        <legend className={styles.title}>Направление</legend>
        <Option
          kind="radio"
          checked={query.direction === null}
          onClick={() => apply({ ...query, direction: null })}
        >
          Все
        </Option>
        {DIRECTIONS.map((direction) => (
          <Option
            key={direction}
            kind="radio"
            checked={query.direction === direction}
            onClick={() => apply({ ...query, direction })}
          >
            {DIRECTION_LABELS[direction]}
          </Option>
        ))}
      </fieldset>
      <fieldset className={styles.group}>
        <legend className={styles.title}>Повод</legend>
        {OCCASIONS.map((occasion) => (
          <Option
            key={occasion}
            kind="check"
            checked={query.occasions.includes(occasion)}
            onClick={() => apply({ ...query, occasions: toggle(query.occasions, occasion) })}
          >
            {OCCASION_LABELS[occasion]}
          </Option>
        ))}
      </fieldset>
      <fieldset className={styles.group}>
        <legend className={styles.title}>Тип товара</legend>
        {TYPES.map((type) => (
          <Option
            key={type}
            kind="check"
            checked={query.types.includes(type)}
            onClick={() => apply({ ...query, types: toggle(query.types, type) })}
          >
            {TYPE_LABELS[type]}
          </Option>
        ))}
      </fieldset>
      {sizes.length > 0 && (
        <fieldset className={styles.group} data-gap>
          <legend className={styles.title}>Размер</legend>
          <div className={styles.sizes}>
            {sizes.map((size) => (
              <button
                key={size}
                type="button"
                className={styles.size}
                aria-pressed={query.size === size}
                onClick={() => apply({ ...query, size: query.size === size ? null : size })}
              >
                {size}
              </button>
            ))}
          </div>
          <Link href={'/catalog/leggings-flare?fit=1'} className={styles.link}>
            Помочь с размером
          </Link>
        </fieldset>
      )}
      {colors.length > 0 && (
        <fieldset className={styles.group} data-gap>
          <legend className={styles.title}>Цвет</legend>
          <div className={styles.colors}>
            {colors.map((color) => (
              <button
                key={color.id}
                type="button"
                className={styles.color}
                aria-pressed={query.color === color.id}
                aria-label={color.name}
                title={color.name}
                onClick={() =>
                  apply({ ...query, color: query.color === color.id ? null : color.id })
                }
              >
                <span className={styles.dot} style={{ '--dot': color.hex }} />
              </button>
            ))}
            {showDemoNotes && <span className={styles.caption}>концепт цветов</span>}
          </div>
        </fieldset>
      )}
      <fieldset className={styles.group}>
        <legend className={styles.title}>Доступность</legend>
        <Option
          kind="radio"
          checked={query.availability === null}
          onClick={() => apply({ ...query, availability: null })}
        >
          Любая
        </Option>
        <Option
          kind="radio"
          checked={query.availability === 'in_stock'}
          onClick={() => apply({ ...query, availability: 'in_stock' })}
        >
          В наличии
        </Option>
        <Option
          kind="radio"
          checked={query.availability === 'preorder'}
          onClick={() => apply({ ...query, availability: 'preorder' })}
        >
          Предзаказ
        </Option>
      </fieldset>
    </div>
  );
}

function Option({
  kind,
  checked,
  onClick,
  children,
}: {
  kind: 'radio' | 'check';
  checked: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={styles.option}
      role={kind === 'radio' ? 'radio' : 'checkbox'}
      aria-checked={checked}
      onClick={onClick}
    >
      <span className={kind === 'radio' ? styles.radio : styles.box} aria-hidden="true">
        {kind === 'check' && checked && <Icon name="check" size={12} strokeWidth={2.4} />}
      </span>
      {children}
    </button>
  );
}

/** Catalog filters stored in the URL: a sticky sidebar on desktop and a sheet on phones. */
export function CatalogFilters({
  query,
  sizes,
  colors,
  total,
  showDemoNotes,
}: CatalogFiltersProps) {
  const router = useRouter();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [pending, startTransition] = useTransition();
  const apply = (next: CatalogQuery) => {
    startTransition(() => router.push(`/catalog${catalogQueryToSearch(next)}`, { scroll: false }));
  };
  const active = !isCatalogQueryEmpty(query);
  return (
    <>
      <aside
        className={cx(styles.sidebar)}
        aria-label="Фильтры каталога"
        aria-busy={pending || undefined}
      >
        <Filters
          query={query}
          sizes={sizes}
          colors={colors}
          showDemoNotes={showDemoNotes}
          apply={apply}
        />
        {active && (
          <Link href="/catalog" className={styles.link} scroll={false}>
            Сбросить фильтры
          </Link>
        )}
      </aside>
      <div className={styles['mobile-bar']}>
        <Button variant="ghost" size="sm" onClick={() => setSheetOpen(true)} aria-haspopup="dialog">
          Фильтры{active ? ' · выбраны' : ''}
        </Button>
      </div>
      <Dialog
        theme="editorial"
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title="Фильтры"
        showTitle
        mobileSheet
        className={styles.sheet}
      >
        <Filters
          query={query}
          sizes={sizes}
          colors={colors}
          showDemoNotes={showDemoNotes}
          apply={apply}
        />
        <div className={styles['sheet-actions']}>
          {active && (
            <Link
              href="/catalog"
              className={styles.link}
              scroll={false}
              onClick={() => setSheetOpen(false)}
            >
              Сбросить
            </Link>
          )}
          <Button onClick={() => setSheetOpen(false)} loading={pending}>
            Показать {total}
          </Button>
        </div>
      </Dialog>
    </>
  );
}
